import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import InformationCard from '@components/Component/InformationCard';
import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import { useAccountabilityFilters, useAccountabilityReport } from './hooks';
import { useUrlState } from './useUrlState';
import AccountabilityGrid, { Expansion, ExpansionKind } from './AccountabilityGrid';
import DefinitionsDialog from './DefinitionsDialog';
import Toolbar from './Toolbar';
import { Bucket, EmptyRowsMode, TeachersMode } from './types';
import { resolveActiveDayTarget } from './intensity';
import { unusedMetricSlugs } from './metricMeta';
import {
  filterEmptyRows,
  filterInactiveTeachers,
  filterRowsByTeacher,
  matchedTeacherAncestorKeys,
} from './rowFilters';
import {
  BetaBadge,
  HEAT_LEVEL_COLOURS,
  LinkButton,
  ZERO_COLOUR,
} from './Accountability.style';

const NO_SLUGS: ReadonlySet<string> = new Set();

const EMPTY_EXPANSION: Expansion = { rows: [], sectionLists: [], sectionBands: [] };

const toggle = (list: string[], key: string): string[] =>
  list.includes(key) ? list.filter((item) => item !== key) : [...list, key];

interface EmptyStateContext {
  search: string;
  teachersMode: TeachersMode;
  emptyRows: EmptyRowsMode;
  /** The report had rows below the district before the toggles and search ran. */
  hadRows: boolean;
}

/** Says why nothing is showing, and which controls bring rows back. */
const emptyStateMessage = ({
  search,
  teachersMode,
  emptyRows,
  hadRows,
}: EmptyStateContext): string => {
  const trimmed = search.trim();
  if (trimmed) {
    const steps: string[] = [];
    if (teachersMode === 'active') {
      steps.push('choose "All"');
    }
    if (emptyRows === 'hide') {
      steps.push('turn off "Hide rows with no active days"');
    }
    const reveal = steps.join(' and ');
    return reveal
      ? `No active teacher matches "${trimmed}". Teachers with no active days are hidden; ${reveal} to include them.`
      : `No teacher matches "${trimmed}".`;
  }
  if (hadRows && emptyRows === 'hide') {
    return 'No teacher had an active day in these dates. Turn off "Hide rows with no active days" to see the empty rows.';
  }
  return teachersMode === 'active'
    ? 'No classes match these filters. Archived classes are left out; choose "All" to include them.'
    : 'No classes match these filters.';
};

const Legend: React.FC<{
  bucket: Bucket;
  activeDayTarget: number;
  onOpenDefinitions: () => void;
}> = ({ bucket, activeDayTarget, onOpenDefinitions }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mt: 1,
      fontSize: '0.7rem',
      color: 'text.secondary',
      flexWrap: 'wrap',
    }}
  >
    <span>No activity</span>
    {HEAT_LEVEL_COLOURS.map((colour) => (
      <Box
        key={colour}
        aria-hidden="true"
        sx={{
          width: '1.4rem',
          height: '0.7rem',
          borderRadius: '2px',
          background: colour,
          border: colour === ZERO_COLOUR ? '1px solid #DDD5CA' : 'none',
        }}
      />
    ))}
    <span>Active every day</span>
    <Box sx={{ ml: 'auto' }}>
      Collapsed rows count <strong>active days</strong> ({`0-${activeDayTarget} per ${bucket}`}).
      Opening a teacher shows <strong>counts per activity type</strong>. The{' '}
      <strong>Total</strong> column adds up each row across the dates. Hover any cell for
      detail, or read the{' '}
      <LinkButton type="button" aria-haspopup="dialog" onClick={onOpenDefinitions}>
        definitions
      </LinkButton>
      .
    </Box>
  </Box>
);

const AccountabilityPage: React.FC = () => {
  const [state, update] = useUrlState();
  const { data, isLoading, isFetching, error } = useAccountabilityReport(state.query);
  const filters = useAccountabilityFilters({
    districtId: state.query.districtId,
    teachersMode: state.query.teachersMode,
  });

  // Expansion is transient view state, not part of the shareable URL: it can
  // explode to unreadable size (per-school teacher keys) and every click
  // would otherwise churn browser history via `update`'s history.replace.
  const [expansion, setExpansion] = useState<Expansion>(EMPTY_EXPANSION);
  const [definitionsOpen, setDefinitionsOpen] = useState(false);

  const onToggle = useCallback((kind: ExpansionKind, key: string) => {
    setExpansion((current) => ({ ...current, [kind]: toggle(current[kind], key) }));
  }, []);

  const openDefinitions = useCallback(() => setDefinitionsOpen(true), []);
  const closeDefinitions = useCallback(() => setDefinitionsOpen(false), []);

  // The district row's own active-days figure is structurally saturated
  // (COUNT DISTINCT day across every teacher in the district is ~always the
  // bucket maximum), so a collapsed landing view is one always-dark row
  // with nothing to explore. Auto-expand the district row -- derived from
  // the report, never hardcoded -- the first time its key becomes known
  // from a loaded report, so the admin lands on the school list instead.
  // The ref makes this a one-time default rather than something that
  // re-fires (and fights a manual collapse) on every refetch, and it
  // deliberately does not also expand schools.
  const didAutoExpandDistrict = useRef(false);
  const districtKey = data?.rows.find((row) => row.type === 'district')?.key ?? null;

  useEffect(() => {
    if (didAutoExpandDistrict.current || !districtKey) return;
    didAutoExpandDistrict.current = true;
    setExpansion((current) =>
      current.rows.includes(districtKey)
        ? current
        : { ...current, rows: [...current.rows, districtKey] },
    );
  }, [districtKey]);

  // The toggles first, then the name search, so a search only ever matches
  // teachers the toggles show.
  const shownRows = useMemo(
    () =>
      data
        ? filterEmptyRows(
            filterInactiveTeachers(data.rows, state.query.teachersMode),
            state.emptyRows,
          )
        : [],
    [data, state.query.teachersMode, state.emptyRows],
  );

  const visibleReport = useMemo(() => {
    if (!data) return null;
    return { ...data, rows: filterRowsByTeacher(shownRows, state.teacherSearch) };
  }, [data, shownRows, state.teacherSearch]);

  const searchAncestorKeys = useMemo(
    () => matchedTeacherAncestorKeys(shownRows, state.teacherSearch),
    [shownRows, state.teacherSearch],
  );

  // What the grid actually receives: the admin's own manual expand/collapse
  // state, plus whatever the live search forces open so a match is
  // reachable. `expansion` (the manual state) is never mutated by a search,
  // so clearing the search box returns the view to exactly what the admin
  // had before typing.
  const effectiveExpansion = useMemo(() => {
    if (searchAncestorKeys.length === 0) return expansion;
    return { ...expansion, rows: Array.from(new Set([...expansion.rows, ...searchAncestorKeys])) };
  }, [expansion, searchAncestorKeys]);

  // Unused means nobody in the whole view used it; hiding teachers or empty
  // rows can't change that.
  const unusedSlugs = useMemo(
    () => (data ? unusedMetricSlugs(data.metrics) : NO_SLUGS),
    [data],
  );
  const hiddenSlugs = state.metricsMode === 'used' ? unusedSlugs : NO_SLUGS;

  // The district row always exists, so "nothing to show" means nothing
  // below it survived the filters.
  const hasRowsToShow = Boolean(
    visibleReport && visibleReport.rows.some((row) => row.type !== 'district'),
  );
  const hadRows = Boolean(data && data.rows.some((row) => row.type !== 'district'));

  const bucket: Bucket = data?.window.bucket ?? state.query.bucket ?? 'week';
  const activeDayTarget = resolveActiveDayTarget(bucket, state.activeDayTarget);

  return (
    <ScrollWrapper sx={{ height: '100%', width: '100%' }}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
            Teacher Accountability
          </Typography>
          <BetaBadge>BETA</BetaBadge>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {data
            ? `${data.district.name}. Active days per ${data.window.bucket}, with activity counts when you open a teacher.`
            : 'Platform activity over a custom time frame.'}
        </Typography>

        <Toolbar
          state={state}
          onChange={update}
          // Before the first response, there is no server-resolved bucket
          // yet, so fall back to the same request-side default used
          // elsewhere (`query.bucket ?? 'week'`); once `data` exists,
          // `data.window.bucket` is the ground truth and is what the
          // Toolbar must label and default against.
          resolvedBucket={bucket}
          filters={filters.data}
          filtersLoading={filters.isLoading}
          filtersError={Boolean(filters.error)}
          unusedMetricCount={unusedSlugs.size}
          onOpenDefinitions={openDefinitions}
        />

        {isLoading && !data && (
          <Box sx={{ minHeight: '24rem', width: '100%' }}>
            <LoadingIndicator content="Building the report…" partial noBackground />
          </Box>
        )}

        {error && (
          <InformationCard
            img="/images/500_error.svg"
            title="Could not load the accountability report"
            description={
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred.'
            }
          />
        )}

        {visibleReport && !hasRowsToShow && (
          <InformationCard
            img="/images/no-content.png"
            title="Nothing to show"
            description={emptyStateMessage({
              search: state.teacherSearch,
              teachersMode: state.query.teachersMode,
              emptyRows: state.emptyRows,
              hadRows,
            })}
          />
        )}

        {visibleReport && hasRowsToShow && (
          <>
            <Box
              sx={{ position: 'relative' }}
              aria-busy={isFetching ? 'true' : undefined}
            >
              <Box
                sx={{
                  opacity: isFetching ? 0.4 : 1,
                  pointerEvents: isFetching ? 'none' : 'auto',
                  transition: 'opacity 0.15s ease',
                }}
              >
                <AccountabilityGrid
                  report={visibleReport}
                  query={state.query}
                  expansion={effectiveExpansion}
                  onToggle={onToggle}
                  hiddenMetricSlugs={hiddenSlugs}
                  emptyRows={state.emptyRows}
                  activeDayTarget={state.activeDayTarget}
                />
              </Box>

              {/* Overlaid, not a sibling that pushes content down, so
                  switching the window/bucket/filters never shifts layout
                  while the new data builds. The grid underneath stays
                  mounted and readable (dimmed) rather than being replaced
                  by a blank state. */}
              {isFetching && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LoadingIndicator content="Updating…" partial noBackground />
                </Box>
              )}
            </Box>
            <Legend
              bucket={visibleReport.window.bucket}
              activeDayTarget={resolveActiveDayTarget(
                visibleReport.window.bucket,
                state.activeDayTarget,
              )}
              onOpenDefinitions={openDefinitions}
            />
          </>
        )}
      </Box>

      <DefinitionsDialog
        open={definitionsOpen}
        onClose={closeDefinitions}
        metrics={data?.metrics ?? []}
        activeDayDescription={data?.active_day_description ?? null}
        unusedSlugs={unusedSlugs}
        bucket={bucket}
        activeDayTarget={activeDayTarget}
      />
    </ScrollWrapper>
  );
};

export default AccountabilityPage;
